package dev.youndie.site.components.widgets

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.silk.components.navigation.Link
import dev.youndie.site.model.Library
import dev.youndie.site.model.SHELL_USER
import org.jetbrains.compose.web.attributes.ATarget
import org.jetbrains.compose.web.attributes.target
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul

/** Renders `youndie@kotlin.website:~$ <command>` above a section. */
@Composable
fun Prompt(command: String, path: String = "~") {
    Div(attrs = { classes("prompt") }) {
        Span(attrs = { classes("user") }) { Text(SHELL_USER) }
        Text(":")
        Span(attrs = { classes("path") }) { Text(path) }
        Text("$ ")
        Span(attrs = { classes("cmd") }) { Text(command) }
    }
}

@Composable
fun SectionHead(title: String, count: String? = null) {
    Div(attrs = { classes("section-head") }) {
        H2 { Text(title) }
        if (count != null) {
            Span(attrs = { classes("count") }) { Text(count) }
        }
    }
}

/**
 * Internal routes (`/about`) go through Silk's [Link] so navigation stays client-side.
 * Anything else renders a plain anchor; http(s) links additionally open in a new tab.
 */
@Composable
fun Chip(href: String, label: String) {
    when {
        href.startsWith("/") -> Link(href, label, Modifier.classNames("chip"))

        href.startsWith("http") -> A(href = href, attrs = {
            classes("chip")
            target(ATarget.Blank)
            attr("rel", "noopener")
        }) { Text(label) }

        else -> A(href = href, attrs = { classes("chip") }) { Text(label) }
    }
}

@Composable
fun LibraryCard(library: Library) {
    // Links to the library's own page, not straight to GitHub. Twelve cards all pointing
    // off-domain hand every visitor and every crawler to somebody else, and leave one
    // crowded home page carrying twelve unrelated subjects.
    Link(library.page, modifier = Modifier.classNames("lib-card")) {
        Div(attrs = { classes("name") }) { Text(library.name) }
        Div(attrs = { classes("tagline") }) { Text(library.tagline) }
        Div(attrs = { classes("tags") }) {
            library.tags.forEachIndexed { index, tag ->
                Span(attrs = {
                    if (index == 0) classes("tag", "accent") else classes("tag")
                }) { Text(tag) }
            }
        }
    }
}

/**
 * One labelled row of the stack list. Twenty tags in a single block read as a
 * dump; split by what the thing is for, the same twenty show shape.
 */
@Composable
fun StackRow(label: String, items: List<String>) {
    Div(attrs = { classes("stack-row") }) {
        Div(attrs = { classes("stack-label") }) { Text(label) }
        Div(attrs = { classes("tags") }) {
            items.forEach { item ->
                Span(attrs = { classes("tag") }) { Text(item) }
            }
        }
    }
}

@Composable
fun TimelineEntry(
    when_: String,
    role: String,
    org: String,
    bullets: List<String>,
) {
    Div(attrs = { classes("entry") }) {
        Div(attrs = { classes("when") }) { Text(when_) }
        Div(attrs = { classes("what") }) {
            H3 { Text(role) }
            Div(attrs = { classes("org") }) { Text(org) }
            if (bullets.isNotEmpty()) {
                Ul {
                    bullets.forEach { Li { Text(it) } }
                }
            }
        }
    }
}
